/* eslint-disable fp/no-mutation */
import { useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Add, Cancel } from '@mui/icons-material'
import LicenseTypeDropdown from 'src/v3/components/form-fields/license-type-dropdown'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import { v4 as uuidv4 } from 'uuid'
import { CREDENTIAL_TYPES } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchRecertCycleTemplates,
  selectRecertCycleTemplates,
  selectRecertCycleTemplatesStatus,
} from 'src/v3/manage/compliance.slice'
import { COMPLIANCE_URLS } from 'src/v3/manage/constants'
import { AddIcon } from 'src/v3/core/icons'
import {
  TemplateCycleType,
  hasNoTypes,
  templateAllowsCycleType,
  templateTypeLabels,
} from 'src/v3/utilities/template-types'
import {
  ComplianceCredentialItemDraft,
  ComplianceLicenseItemDraft,
  ComplianceNREMTItemDraft,
  ComplianceRecertCycleItemDraft,
} from '../../types'

type RequirementType = 'license' | 'credential' | 'nremt' | 'recert'
type RecertCycleType = 'license' | 'credential' | 'nremt'

type Props = {
  isOpen: boolean
  linkedServiceID: string
  existingLicenseItems: ComplianceLicenseItemDraft[]
  existingCredentialItems: ComplianceCredentialItemDraft[]
  existingNREMTItems: ComplianceNREMTItemDraft[]
  existingRecertItems: ComplianceRecertCycleItemDraft[]
  onClose: () => void
  onAddLicenseItem: (item: ComplianceLicenseItemDraft) => void
  onAddCredentialItem: (item: ComplianceCredentialItemDraft) => void
  onAddNREMTItem: (item: ComplianceNREMTItemDraft) => void
  onAddRecertItem: (item: ComplianceRecertCycleItemDraft) => void
}

export default function AddRequirementModal({
  isOpen,
  linkedServiceID,
  existingLicenseItems,
  existingCredentialItems,
  existingNREMTItems,
  existingRecertItems,
  onClose,
  onAddLicenseItem,
  onAddCredentialItem,
  onAddNREMTItem,
  onAddRecertItem,
}: Props) {
  let dispatch = useAppDispatch()
  let templates = useAppSelector(selectRecertCycleTemplates(linkedServiceID))
  let templatesStatus = useAppSelector(
    selectRecertCycleTemplatesStatus(linkedServiceID),
  )

  useEffect(() => {
    if (
      isOpen &&
      linkedServiceID &&
      (!templatesStatus || templatesStatus === 'uninitialized')
    ) {
      dispatch(fetchRecertCycleTemplates(linkedServiceID))
    }
  }, [isOpen, linkedServiceID, templatesStatus])
  let [requirementType, setRequirementType] =
    useState<RequirementType>('license')

  // license fields
  let [state, setState] = useState('unselected')
  let [licenseType, setLicenseType] = useState('unselected')

  // credential fields
  let [credentialType, setCredentialType] = useState('unselected')
  let [customCredentialType, setCustomCredentialType] = useState('')

  // recert fields
  let [recertCycleType, setRecertCycleType] =
    useState<RecertCycleType>('license')
  let [recertTemplateID, setRecertTemplateID] = useState('unselected')
  let [recertLicenseType, setRecertLicenseType] = useState('unselected')
  let [recertState, setRecertState] = useState('unselected')
  let [recertCredentialType, setRecertCredentialType] = useState('unselected')
  let [recertCustomCredentialType, setRecertCustomCredentialType] = useState('')

  // Only the templates whose types cover this cycle type; the API rejects any other.
  let recertTemplateCycleType: TemplateCycleType | undefined =
    recertCycleType === 'nremt'
      ? { kind: 'nremt' }
      : recertCycleType === 'license'
        ? recertLicenseType !== 'unselected' && recertState !== 'unselected'
          ? { kind: 'license', type: recertLicenseType, state: recertState }
          : undefined
        : recertCredentialType !== 'unselected'
          ? {
              kind: 'credential',
              type:
                recertCredentialType === 'Other'
                  ? recertCustomCredentialType.trim()
                  : recertCredentialType,
            }
          : undefined
  let recertTemplates = (templates ?? []).filter(
    (t) =>
      !recertTemplateCycleType ||
      templateAllowsCycleType(t.types, recertTemplateCycleType),
  )

  useEffect(() => {
    if (
      recertTemplateID !== 'unselected' &&
      !recertTemplates.some((t) => t.id === recertTemplateID)
    )
      setRecertTemplateID('unselected')
  }, [
    templates,
    recertTemplateID,
    recertCycleType,
    recertLicenseType,
    recertState,
    recertCredentialType,
    recertCustomCredentialType,
  ])

  let isDuplicateLicense = existingLicenseItems.some(
    (i) => i.type === licenseType && i.state === state,
  )
  let isDuplicateCredential = existingCredentialItems.some(
    (i) =>
      i.type === credentialType &&
      (credentialType !== 'Other' ||
        i.customType === customCredentialType.trim()),
  )
  let nremtAlreadyAdded = existingNREMTItems.length > 0
  let isDuplicateRecertNremt =
    recertCycleType === 'nremt' &&
    existingRecertItems.some((i) => i.cycleType === 'nremt')
  let isDuplicateRecertLicense =
    recertCycleType === 'license' &&
    existingRecertItems.some(
      (i) =>
        i.cycleType === 'license' &&
        i.licenseType?.toLowerCase() === recertLicenseType.toLowerCase() &&
        i.state === recertState,
    )
  let isDuplicateRecertCredential =
    recertCycleType === 'credential' &&
    existingRecertItems.some(
      (i) =>
        i.cycleType === 'credential' &&
        i.credentialType === recertCredentialType &&
        (recertCredentialType !== 'Other' ||
          i.customCredentialType === recertCustomCredentialType.trim()),
    )

  let canAdd = (() => {
    if (requirementType === 'license')
      return (
        state !== 'unselected' &&
        licenseType !== 'unselected' &&
        !isDuplicateLicense
      )
    if (requirementType === 'credential')
      return (
        credentialType !== 'unselected' &&
        (credentialType !== 'Other' ||
          customCredentialType.trim().length > 0) &&
        !isDuplicateCredential
      )
    if (requirementType === 'nremt') return !nremtAlreadyAdded
    if (recertCycleType === 'nremt') return !isDuplicateRecertNremt
    if (recertCycleType === 'license')
      return (
        recertState !== 'unselected' &&
        recertLicenseType !== 'unselected' &&
        !isDuplicateRecertLicense
      )
    return (
      recertCredentialType !== 'unselected' &&
      (recertCredentialType !== 'Other' ||
        recertCustomCredentialType.trim().length > 0) &&
      !isDuplicateRecertCredential
    )
  })()

  let handleClose = () => {
    setRequirementType('license')
    setState('unselected')
    setLicenseType('unselected')
    setCredentialType('unselected')
    setCustomCredentialType('')
    setRecertCycleType('license')
    setRecertTemplateID('unselected')
    setRecertLicenseType('unselected')
    setRecertState('unselected')
    setRecertCredentialType('unselected')
    setRecertCustomCredentialType('')
    onClose()
  }

  let handleAdd = () => {
    if (requirementType === 'license') {
      onAddLicenseItem({ id: uuidv4(), state, type: licenseType })
    } else if (requirementType === 'credential') {
      onAddCredentialItem({
        id: uuidv4(),
        type: credentialType,
        ...(credentialType === 'Other'
          ? { customType: customCredentialType.trim() }
          : {}),
      })
    } else if (requirementType === 'nremt') {
      onAddNREMTItem({ id: uuidv4() })
    } else {
      let draft: ComplianceRecertCycleItemDraft = {
        id: uuidv4(),
        cycleType: recertCycleType,
        templateID:
          recertTemplateID !== 'unselected' ? recertTemplateID : undefined,
      }
      if (recertCycleType === 'license') {
        draft.licenseType = recertLicenseType
        draft.state = recertState
      } else if (recertCycleType === 'credential') {
        draft.credentialType = recertCredentialType
        if (recertCredentialType === 'Other')
          draft.customCredentialType = recertCustomCredentialType.trim()
      }
      onAddRecertItem(draft)
    }
    handleClose()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="add-requirement-dialog-title"
    >
      <DialogTitle id="add-requirement-dialog-title" className="pt-6">
        Add Requirement
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        <ToggleButtonGroup
          color="primary"
          value={requirementType}
          exclusive
          onChange={(_, next) => next && setRequirementType(next)}
          aria-label="Requirement type"
          fullWidth
        >
          <ToggleButton value="license">License</ToggleButton>
          <ToggleButton value="credential">Credential</ToggleButton>
          <ToggleButton value="nremt">NREMT</ToggleButton>
          <ToggleButton value="recert">Recertification</ToggleButton>
        </ToggleButtonGroup>

        {requirementType === 'license' && (
          <Box className="flex flex-col gap-4">
            <StateDropdown
              variant="outlined"
              bgDefaultColor="background.paper"
              value={state}
              onChange={(e) => setState(e.target.value as string)}
            />
            <LicenseTypeDropdown
              variant="outlined"
              bgDefaultColor="background.paper"
              value={licenseType}
              onChange={(e) => setLicenseType(e.target.value as string)}
            />
            {isDuplicateLicense && (
              <Alert severity="warning">
                A requirement for this license type and state already exists.
              </Alert>
            )}
          </Box>
        )}

        {requirementType === 'credential' && (
          <Box className="flex flex-col gap-4">
            <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
              <InputLabel id="credential-type-label">Type</InputLabel>
              <Select
                labelId="credential-type-label"
                label="Type"
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value as string)}
              >
                <MenuItem value="unselected">Unselected</MenuItem>
                {CREDENTIAL_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {credentialType === 'Other' && (
              <TextField
                label="Custom Credential Type"
                fullWidth
                value={customCredentialType}
                onChange={(e) => setCustomCredentialType(e.target.value)}
                sx={{ bgcolor: 'background.paper' }}
              />
            )}
            {isDuplicateCredential && (
              <Alert severity="warning">
                A requirement for this credential type already exists.
              </Alert>
            )}
          </Box>
        )}

        {requirementType === 'nremt' && (
          <Box className="flex flex-col gap-4">
            <Typography variant="body2" color="text.secondary">
              Requires users to have a verified NREMT record on file.
            </Typography>
            {nremtAlreadyAdded && (
              <Alert severity="warning">
                An NREMT requirement already exists.
              </Alert>
            )}
          </Box>
        )}

        {requirementType === 'recert' && (
          <Box className="flex flex-col gap-4">
            <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
              <InputLabel id="recert-cycle-type-label">Cycle Type</InputLabel>
              <Select
                labelId="recert-cycle-type-label"
                label="Cycle Type"
                value={recertCycleType}
                onChange={(e) => {
                  setRecertCycleType(e.target.value as RecertCycleType)
                  setRecertLicenseType('unselected')
                  setRecertState('unselected')
                  setRecertCredentialType('unselected')
                  setRecertCustomCredentialType('')
                }}
              >
                <MenuItem value="license">License</MenuItem>
                <MenuItem value="credential">Credential</MenuItem>
                <MenuItem value="nremt">NREMT</MenuItem>
              </Select>
            </FormControl>

            {recertCycleType === 'license' && (
              <>
                <StateDropdown
                  variant="outlined"
                  bgDefaultColor="background.paper"
                  value={recertState}
                  onChange={(e) => setRecertState(e.target.value as string)}
                />
                <LicenseTypeDropdown
                  variant="outlined"
                  bgDefaultColor="background.paper"
                  value={recertLicenseType}
                  onChange={(e) =>
                    setRecertLicenseType(e.target.value as string)
                  }
                />
              </>
            )}

            {recertCycleType === 'credential' && (
              <>
                <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
                  <InputLabel id="recert-credential-type-label">
                    Credential Type
                  </InputLabel>
                  <Select
                    labelId="recert-credential-type-label"
                    label="Credential Type"
                    value={recertCredentialType}
                    onChange={(e) =>
                      setRecertCredentialType(e.target.value as string)
                    }
                  >
                    <MenuItem value="unselected">Unselected</MenuItem>
                    {CREDENTIAL_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {recertCredentialType === 'Other' && (
                  <TextField
                    label="Custom Credential Type"
                    fullWidth
                    value={recertCustomCredentialType}
                    onChange={(e) =>
                      setRecertCustomCredentialType(e.target.value)
                    }
                    sx={{ bgcolor: 'background.paper' }}
                  />
                )}
              </>
            )}
            <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
              <InputLabel id="recert-template-label">
                Template (optional)
              </InputLabel>
              <Select
                labelId="recert-template-label"
                label="Template (optional)"
                value={recertTemplateID}
                onChange={(e) => {
                  if (e.target.value === 'CREATE_NEW') {
                    window.open(
                      COMPLIANCE_URLS.TEMPLATES,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  } else {
                    setRecertTemplateID(e.target.value)
                  }
                }}
              >
                <MenuItem value="unselected">None</MenuItem>
                <MenuItem
                  value="CREATE_NEW"
                  sx={{ color: 'primary.main', fontWeight: 'medium' }}
                >
                  <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                  Create New Template
                </MenuItem>
                {recertTemplates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Box>
                      <Typography>{t.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasNoTypes(t.types)
                          ? 'Every cycle'
                          : templateTypeLabels(t.types).join(' · ')}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isDuplicateRecertNremt && (
              <Alert severity="warning">
                A recertification requirement for NREMT already exists.
              </Alert>
            )}
            {isDuplicateRecertLicense && (
              <Alert severity="warning">
                A recertification requirement for this license type and state
                already exists.
              </Alert>
            )}
            {isDuplicateRecertCredential && (
              <Alert severity="warning">
                A recertification requirement for this credential type already
                exists.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions className="pb-4 pr-4">
        <Button variant="outlined" onClick={handleClose} startIcon={<Cancel />}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!canAdd}
          startIcon={<Add />}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}
